import { createContext, useContext, type ComponentType, type ReactNode } from 'react';

/**
 * Shape of the document-surfaces orb. The concrete renderer lives in
 * `features/document-surfaces` and pulls the whole panel with it, so it cannot
 * move to `lib/`. Registering it through this context lets `features/editor`
 * render the orb inside its own layout without a cross-feature import
 * (`import/no-restricted-paths`). The renderer is mounted at `app/router.tsx`,
 * mirroring `document-editor-context.tsx` and `canvas-preview-context.tsx`.
 *
 * `onActiveViewChange` lets the renderer report its active surface upward:
 * `features/document-surfaces` owns which surface is active, but `Actionbar`
 * (which needs that value as `presenceViewId`) is mounted by `features/editor`
 * — a different ESLint zone. This seam is how the value crosses without a
 * cross-feature import.
 *
 * `canEdit` carries the opposite direction: `features/editor` derives the
 * viewer's edit permission (from the sharing permission, not from its own
 * `readOnly` display flag — see rich-text-editor.tsx), and the renderer needs
 * it to decide whether to show mutating affordances (create/rename/move/
 * delete) or just let the viewer read and switch surfaces (ui-design.md
 * §9.6). Defaults to true when omitted.
 */
export type SurfaceOrbRenderer = ComponentType<{
  entityId: string;
  onActiveViewChange?: (viewId: string) => void;
  canEdit?: boolean;
}>;

const SurfaceOrbContext = createContext<SurfaceOrbRenderer | null>(null);

interface SurfaceOrbProviderProps {
  renderer: SurfaceOrbRenderer;
  children: ReactNode;
}

export function SurfaceOrbProvider({ renderer, children }: SurfaceOrbProviderProps) {
  return (
    <SurfaceOrbContext.Provider value={renderer}>
      {children}
    </SurfaceOrbContext.Provider>
  );
}

/**
 * The registered orb renderer, or `null` when no provider is mounted (unit
 * tests, the share view, the recording HUD). Callers render nothing when null.
 */
export function useSurfaceOrb(): SurfaceOrbRenderer | null {
  return useContext(SurfaceOrbContext);
}
